import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

// config 
import config from '../../data/layout-config';

// custom hooks
import useRouteMatch from '../../hooks/useRouteMatch';

// components
import { NioBrand, NioIcon } from '..';
import { useTranslation } from '../../i18n/I18nProvider';
import { useData } from '../../context/DataProvider/DataProvider';

export default function Footer({ variant = 1, children }) {
  const { t } = useTranslation();
  const data = useData();
  const ss = data?.siteSettings || {};
  const companyName = ss.company?.name || 'Alo Alla';
  const contactEmail = ss.contact?.email || '';
  const contactPhone = ss.contact?.phone || '';
  const contactAddress = ss.contact?.address || '';
  const socialFacebook = ss.social?.facebook || '';
  const socialInstagram = ss.social?.instagram || '';
  const socialLinkedin = ss.social?.linkedin || '';
  const socialTwitter = ss.social?.twitter || '';
  const socialWhatsapp = ss.social?.whatsapp || '';
  const copyrightText = ss.footer?.copyright || '';
  const termsUrl = ss.footer?.termsUrl || '/terms-and-conditions';
  const privacyUrl = ss.footer?.privacyUrl || '#';

  const compClasses = classNames({
    [`${useRouteMatch("/") ? "nk-footer-landing" : "nk-footer"}`]: true,

    // bg color
    "bg-dark": useRouteMatch(["/index-crypto-profile"]),
    "bg-blue-1700": useRouteMatch(["/index-bs-analytics"]),
    "bg-gray-1200": useRouteMatch(["/index-bs-consulting", "/index-fintech"]),
    "bg-gray-1100": useRouteMatch(["/index-crypto-profile", "/index-coaching-service"]),
    "is-theme": useRouteMatch(["/index-bs-analytics", "/index-coaching-service", "/index-crypto-profile", "/index-bs-consulting", "/index-fintech"])
  });


  const footerTextClasses = classNames({
    // typography
    "fs-16": useRouteMatch(["/index-kids-course"]),
    "fw-medium": useRouteMatch(["/index-kids-course"]),
    "text-capitalize": true,

    // colors
    "text-dark": !useRouteMatch(["/index-crypto-profile"]),
  });

  // variants of navbar 
  function filterDataByVariant(variantNumber) {
    return config.filter(item => item.variant === variantNumber);
  }
  const [layoutConfig] = filterDataByVariant(variant)

  return (
    <footer className={compClasses}>
      {
        layoutConfig.footer.variant === 1 ?
          <>
            <div className="call-to">
              <div className="nk-mask z-1 blur-6"></div>
              <Container>
                <div className="call-to-content z-1 position-relative is-theme py-7">
                  <Row className="justify-content-center">
                    <Col xl={6}>
                      <div className="call-to-info text-center">
                        <NioBrand className="mb-3" logo={layoutConfig.footer.logo?.name || "s1"} variant={layoutConfig.footer.logo?.variant || "dark"} />
                        <h2 className="mb-1">{t('footer.ctaTitle')}</h2>
                        <p className="m-0">{t('footer.ctaBody')}</p>
                        <div className="call-to-action pt-5 pt-lg-7">
                          <Link to="/user-request" className="btn btn-primary mb-1">
                            <NioIcon name="edit" className="me-2" />
                            {t('footer.ctaButton')}
                          </Link>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Container>
            </div>
            <div className="nk-footer-landing-copyright py-3">
              <Container>
                <p className="mb-0 text-capitalize">
                  Copyright &copy; <span className="text-white">{new Date().getFullYear()}</span> {companyName}. {copyrightText || t('footer.rights')}
                </p>
              </Container>
            </div>
          </>
          :
          layoutConfig.footer.variant === 2 ?
            <section className={compClasses}>
              <div className="nk-footer-top">
                <Container>
                  <Row className="nk-footer-content justify-content-xl-between">
                    <Col md={8} lg={4} xl={4}>
                      <div className="nk-footer-brand pb-5 pb-lg-0">
                        <div className="nk-footer-brand-info mb-4">
                          <div className="nk-footer-logo">
                            <NioBrand logo={layoutConfig.footer.logo?.name} variant={layoutConfig.footer.logo?.variant} />
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col lg={8} xl={6}>
                      <Row className="justify-content-between">
                        <Col sm={8} md={7}>
                          <div className="nk-footer-info">
                            <h5 className="title">{t('footer.pages')}</h5>
                            <ul className="row gy-1 gy-sm-4">
                              <li className="col-6">
                                <Link to="/">{t('footer.home')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/provider-signup">{t('footer.becomeProvider')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/user-request">{t('footer.publishRequest')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/support-sav">{t('footer.afterSales')}</Link>
                              </li>
                             
                            </ul>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="nk-footer-info">
                            <h5 className="title">{t('footer.contact')}</h5>
                            <ul className="row gy-1 gy-sm-4">
                              <li className="col-12">
                                <Link to="/auth/login">{t('footer.login')}</Link>
                              </li>
                              <li className="col-12">
                                <Link to="/auth/sign-up">{t('footer.signup')}</Link>
                              </li>
                              {contactEmail && <li className="col-12"><a href={`mailto:${contactEmail}`}>{contactEmail}</a></li>}
                              {contactPhone && <li className="col-12"><a href={`tel:${contactPhone}`}>{contactPhone}</a></li>}
                              {contactAddress && <li className="col-12"><span>{contactAddress}</span></li>}
                            </ul>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </div>
              <div className="nk-footer-bottom">
                <Container>
                  <Row className="nk-footer-content justify-content-between">
                    <Col lg={6} className="px-0">
                      <p className="nk-footer-copyright-text text-center text-lg-start">&copy; <span id="currentYear"> {new Date().getFullYear()} </span>
                        {companyName}. {copyrightText || t('footer.rights')}
                      </p>
                    </Col>
                    <Col lg={6} className="px-0">
                      <ul className="nk-footer-copyright justify-content-center justify-content-lg-end">
                        <li>
                          <Link className={footerTextClasses} to={termsUrl}>{t('footer.terms')}</Link>
                        </li>
                        <li>
                          <Link className={footerTextClasses} to={privacyUrl}>{t('footer.privacy')}</Link>
                        </li>
                        {socialFacebook && <li><a href={socialFacebook} target="_blank" rel="noreferrer" className={footerTextClasses}>Facebook</a></li>}
                        {socialInstagram && <li><a href={socialInstagram} target="_blank" rel="noreferrer" className={footerTextClasses}>Instagram</a></li>}
                        {socialLinkedin && <li><a href={socialLinkedin} target="_blank" rel="noreferrer" className={footerTextClasses}>LinkedIn</a></li>}
                        {socialTwitter && <li><a href={socialTwitter} target="_blank" rel="noreferrer" className={footerTextClasses}>X / Twitter</a></li>}
                        {socialWhatsapp && <li><a href={`https://wa.me/${socialWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className={footerTextClasses}>WhatsApp</a></li>}
                      </ul>
                    </Col>
                  </Row>
                </Container>
              </div>
            </section>
            :
            <>
              {children}
            </>
      }
    </footer >
  )
}